import { requireApiOrigin } from "@utils/apiOrigin";
import { authenticatedFetch } from "@utils/authenticatedFetch";

function apiBase(): string {
  return requireApiOrigin();
}

function formatNetworkApiError(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (!item || typeof item !== "object") return "";
        const msg = (item as { msg?: string }).msg;
        const loc = (item as { loc?: unknown[] }).loc;
        if (msg && Array.isArray(loc) && loc.length) {
          return `${loc.slice(1).join(".")}: ${msg}`;
        }
        return msg ?? "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

/** Keep in sync with `_REQUIRED_IMPORT_COLUMNS` in network_management_service.py */
export const NETWORK_IMPORT_REQUIRED_COLUMNS = [
  "CMS Certification Number (CCN)",
  "Provider Name",
  "Provider Address",
  "City/Town",
  "State",
  "ZIP Code",
] as const;

/** Keep in sync with `_OPTIONAL_IMPORT_COLUMNS` in network_management_service.py */
export const NETWORK_IMPORT_OPTIONAL_COLUMNS = [
  "Telephone Number",
  "Legal Business Name",
] as const;

export interface NetworkRecord {
  id: number;
  name: string;
  legal_business_name?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  network_group_id: string;
  country_code: string;
  vertical: string;
  is_active: boolean;
  child_facility_count: number;
  linked_ccn_count: number;
  /** Distinct state codes for linked child facilities, sorted (e.g. ["CA", "OR", "WA"]). */
  facility_states?: string[];
}

export interface NetworkCreatePayload {
  name: string;
  legal_business_name?: string;
  address_1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
}

export interface NetworkImportErrorRow {
  row: number;
  ccn?: string | null;
  reason: string;
}

export type NetworkImportOutcome = "created" | "linked" | "skipped" | "error";

export interface NetworkImportFacilityRow {
  row: number;
  ccn: string;
  provider_name: string;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  telephone_number?: string | null;
  legal_business_name?: string | null;
  outcome: NetworkImportOutcome;
  reason?: string | null;
}

export interface NetworkImportResult {
  network_id: number;
  network_group_id: string;
  parsed_row_count: number;
  created_count: number;
  linked_count: number;
  skipped_count: number;
  error_count: number;
  errors: NetworkImportErrorRow[];
  errors_truncated?: boolean;
  facilities: NetworkImportFacilityRow[];
  batches_committed?: number;
}

export interface NetworkLinkedFacilityPage {
  total: number;
  offset: number;
  limit: number;
  items: NetworkLinkedFacility[];
}

export interface DeleteNetworkResult {
  network_id: number;
  network_name: string;
  unlinked_count: number;
  deleted_orphan_count: number;
}

export async function createNetwork(payload: NetworkCreatePayload): Promise<NetworkRecord> {
  const res = await authenticatedFetch(`${apiBase()}/api/v1/network-management/networks`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Create network failed (${res.status})`));
  }
  return res.json();
}

const NETWORKS_CACHE_TTL_MS = 60_000;
let _networksCache: { at: number; rows: NetworkRecord[] } | null = null;
let _networksInFlight: Promise<NetworkRecord[]> | null = null;
let _networksCacheGen = 0;

/** Clears cached network list (e.g. after create/delete). */
export function invalidateNetworksCache(): void {
  _networksCache = null;
  _networksInFlight = null;
  _networksCacheGen += 1;
}

export async function listNetworks(options?: {
  bypassCache?: boolean;
}): Promise<NetworkRecord[]> {
  const now = Date.now();
  if (
    !options?.bypassCache &&
    _networksCache &&
    now - _networksCache.at < NETWORKS_CACHE_TTL_MS
  ) {
    return _networksCache.rows;
  }
  // bypassCache must not reuse an in-flight response that may predate a delete/create.
  if (!options?.bypassCache && _networksInFlight) {
    return _networksInFlight;
  }

  const run = async (): Promise<NetworkRecord[]> => {
    const gen = _networksCacheGen;
    const res = await authenticatedFetch(`${apiBase()}/api/v1/network-management/networks`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatNetworkApiError(err, `List networks failed (${res.status})`));
    }
    const data = await res.json();
    const rows = Array.isArray(data) ? data : [];
    // Ignore stale responses after invalidateNetworksCache() during this request.
    if (gen === _networksCacheGen) {
      _networksCache = { at: Date.now(), rows };
    }
    return rows;
  };

  const promise = run().finally(() => {
    if (_networksInFlight === promise) {
      _networksInFlight = null;
    }
  });
  _networksInFlight = promise;
  return promise;
}

export interface NetworkLinkedFacility {
  id: number;
  facility_name: string;
  city?: string | null;
  state?: string | null;
  ccn?: string | null;
}

export async function listNetworkFacilities(
  networkId: number,
  options?: { offset?: number; limit?: number },
): Promise<NetworkLinkedFacilityPage> {
  const offset = options?.offset ?? 0;
  const limit = options?.limit ?? 100;
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/networks/${networkId}/facilities?${params}`,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `List network facilities failed (${res.status})`));
  }
  const data = await res.json();
  if (data && typeof data === "object" && Array.isArray((data as NetworkLinkedFacilityPage).items)) {
    return data as NetworkLinkedFacilityPage;
  }
  return { total: 0, offset, limit, items: [] };
}

export async function getNetwork(networkId: number): Promise<NetworkRecord> {
  const res = await authenticatedFetch(`${apiBase()}/api/v1/network-management/networks/${networkId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Get network failed (${res.status})`));
  }
  return res.json();
}

export async function importNetworkFacilities(
  networkId: number,
  file: File,
  options?: { includeFacilities?: boolean },
): Promise<NetworkImportResult> {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams();
  if (options?.includeFacilities) {
    params.set("include_facilities", "true");
  }
  const qs = params.toString();
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/networks/${networkId}/import-facilities${qs ? `?${qs}` : ""}`,
    { method: "POST", body: form },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Import failed (${res.status})`));
  }
  const data = (await res.json()) as NetworkImportResult;
  return {
    ...data,
    facilities: Array.isArray(data.facilities) ? data.facilities : [],
  };
}

export async function deleteNetwork(networkId: number): Promise<DeleteNetworkResult> {
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/networks/${networkId}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Delete network failed (${res.status})`));
  }
  // Drop list cache immediately so post-delete UI cannot resurrect the row.
  invalidateNetworksCache();
  const raw = await res.text();
  if (!raw.trim()) {
    return {
      network_id: networkId,
      network_name: "",
      unlinked_count: 0,
      deleted_orphan_count: 0,
    };
  }
  try {
    return JSON.parse(raw) as DeleteNetworkResult;
  } catch {
    throw new Error("Delete network succeeded but returned an invalid response");
  }
}

export interface NetworkChainSearchResult {
  chain_id: string;
  chain_name: string;
  facility_count: number;
  states: string[];
}

export async function searchChains(query: string, limit = 20): Promise<NetworkChainSearchResult[]> {
  const params = new URLSearchParams({ q: query.trim(), limit: String(limit) });
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/chains/search?${params}`,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Chain search failed (${res.status})`));
  }
  const data = await res.json();
  return Array.isArray(data.chains) ? data.chains : [];
}

export async function importChain(
  networkId: number,
  chainId: string,
): Promise<NetworkImportResult> {
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/networks/${networkId}/import-chain`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chain_id: chainId }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Chain import failed (${res.status})`));
  }
  return res.json();
}

export async function importIdentifiers(
  networkId: number,
  identifierType: "npi" | "ccn",
  identifiers: string[],
): Promise<NetworkImportResult> {
  const res = await authenticatedFetch(
    `${apiBase()}/api/v1/network-management/networks/${networkId}/import-identifiers`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier_type: identifierType, identifiers }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatNetworkApiError(err, `Identifier import failed (${res.status})`));
  }
  return res.json();
}
